import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import DevicesIcon from '@mui/icons-material/Devices';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import notificationApi from '../../../services/notificationApi';
import { usePushNotifications } from '../../../contexts/PushNotificationContext';

const NotificationsSettings = () => {
  const { enablePush, disablePush, isPushSupported, getPushPermission, isCurrentlySubscribed } =
    usePushNotifications() || {};

  const [prefs, setPrefs]         = useState({ push: true, email: false });
  const [subscribed, setSubscribed] = useState(false);
  const [devices, setDevices]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [testing, setTesting]       = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [snack, setSnack]           = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prefData, deviceList, subbed] = await Promise.all([
        notificationApi.getPreferences(),
        notificationApi.listSubscriptions(),
        isCurrentlySubscribed ? isCurrentlySubscribed() : Promise.resolve(false),
      ]);
      setPrefs(prefData);
      setDevices(deviceList);
      setSubscribed(subbed);
    } catch {
      showSnack('Failed to load notification settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [isCurrentlySubscribed]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handlePrefToggle = async (channel, value) => {
    const updated = { ...prefs, [channel]: value };
    setPrefs(updated);
    setSaving(true);
    try {
      await notificationApi.setPreferences({ [channel]: value });
      showSnack('Preferences saved');
    } catch {
      setPrefs(prefs); // revert
      showSnack('Failed to save preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePush = async () => {
    if (!isPushSupported) {
      showSnack('Push notifications are not supported in this browser', 'warning');
      return;
    }
    setSubscribing(true);
    try {
      const result = await enablePush();
      if (result.ok) {
        showSnack('Push notifications enabled on this device');
        await loadAll();
      } else {
        showSnack(result.error || 'Could not enable push notifications', 'error');
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleDisablePush = async () => {
    setSubscribing(true);
    try {
      const result = await disablePush();
      if (result.ok) {
        showSnack('Push notifications disabled for this device');
        await loadAll();
      } else {
        showSnack(result.error || 'Could not disable push notifications', 'error');
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    try {
      await notificationApi.sendTestNotification();
      showSnack('Test notification sent! Check your device.');
    } catch {
      showSnack('Failed to send test notification', 'error');
    } finally {
      setTesting(false);
    }
  };

  const permission = getPushPermission?.() ?? 'unsupported';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Channel preferences ─────────────────────────────────────── */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Notification Channels
      </Typography>
      <List dense disablePadding sx={{ mb: 3 }}>
        <ListItem disablePadding sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <NotificationsActiveIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Push Notifications"
            secondary="Receive real-time alerts on your device"
          />
          <ListItemSecondaryAction>
            <Switch
              checked={prefs.push}
              onChange={(e) => handlePrefToggle('push', e.target.checked)}
              disabled={saving}
              size="small"
            />
          </ListItemSecondaryAction>
        </ListItem>
        <Divider component="li" />
        <ListItem disablePadding sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EmailIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary="Email Notifications"
            secondary="Receive alerts via email (coming soon)"
          />
          <ListItemSecondaryAction>
            <Switch
              checked={prefs.email}
              onChange={(e) => handlePrefToggle('email', e.target.checked)}
              disabled={saving}
              size="small"
            />
          </ListItemSecondaryAction>
        </ListItem>
      </List>

      <Divider sx={{ mb: 3 }} />

      {/* ── This device ─────────────────────────────────────────────── */}
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        This Device
      </Typography>

      {!isPushSupported && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Push notifications are not supported in this browser.
        </Alert>
      )}

      {isPushSupported && permission === 'denied' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Notification permission is blocked. Please allow notifications in your browser settings and reload.
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {subscribed ? (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleDisablePush}
            disabled={subscribing || !isPushSupported}
            startIcon={subscribing ? <CircularProgress size={14} /> : null}
          >
            Disable push on this device
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={handleEnablePush}
            disabled={subscribing || !isPushSupported || permission === 'denied'}
            startIcon={subscribing ? <CircularProgress size={14} color="inherit" /> : <DevicesIcon fontSize="small" />}
          >
            Enable push on this device
          </Button>
        )}

        {subscribed && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleTestNotification}
            disabled={testing}
            startIcon={testing ? <CircularProgress size={14} /> : null}
          >
            {testing ? 'Sending…' : 'Send test notification'}
          </Button>
        )}
      </Stack>

      {/* ── Registered devices ──────────────────────────────────────── */}
      {devices.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Registered Devices ({devices.length})
          </Typography>
          <List dense disablePadding>
            {devices.map((d) => (
              <ListItem key={d.id} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <SmartphoneIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={`${d.platform || 'web'} device`}
                  secondary={d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsSettings;
